import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Mail, UserPlus, Trash2 } from "lucide-react";
import { adminOverview, adminUsers, adminUser, adminSetProductRole, adminClearProductRole, adminSetPlatformAdmin, adminSetUserEmail, adminCreateUser, adminDeleteUser, adminEmailCheck, adminSetSchoolPrincipal, adminSchools, adminSchool, adminSchoolStudent, adminClasses, adminClassApprove, adminClassReject, adminSetSchoolLicense, adminDeleteSchool, adminSchoolAppRequests, adminAppRequestApprove, adminAppRequestDecline, errorMessage } from "./api";
import { card, h2, th, td, field, Bar, Modal, CredentialsModal, useEmailLookup, EmailLookupNote, LICENSE_APPS, DEFAULT_LICENSE_APPS, AppPicker } from "./ui";

const when = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const KIND = { individual: "Individual", organisation: "Principal", teacher: "Teacher", student: "Student", other: "Migrated" };

function ProgressCards({ apps }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {apps.map((p) => (
        <div key={p.product} className={card}>
          <div className="flex justify-between"><div className="font-semibold text-forest-700">{p.name}</div>{p.status === "ok" && <div className="text-sm font-semibold">{p.percent}%</div>}</div>
          {p.status === "ok" ? (<><div className="mt-2"><Bar percent={p.percent} /></div><div className="mt-2 text-sm text-foreground/70">{p.summary}</div></>)
            : <div className="mt-2 text-sm text-foreground/60">{p.status === "not_started" ? "Hasn't opened this app yet" : "Couldn't load right now"}</div>}
        </div>
      ))}
    </div>
  );
}

function Back({ onClick, label }) {
  return <button type="button" onClick={onClick} className="text-sm text-forest-700 hover:underline inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" />{label}</button>;
}

// ------------------------------------------------------------------ overview

export function AdminOverview({ token, go }) {
  const [d, setD] = useState(null);
  useEffect(() => { adminOverview(token).then(setD).catch((e) => toast.error(errorMessage(e))); }, [token]);
  if (!d) return <div className="text-forest-700">Loading…</div>;
  const tiles = [
    ["Total users", d.total_users, "users"], ["Individuals", d.users.individual, "users"], ["Schools", d.schools, "schools"],
    ["Teachers", d.users.teacher], ["Students", d.users.student], ["Rosters waiting", d.rosters_waiting, "inbox"], ["Unread inquiries", d.unread_inquiries, "inbox"],
    ["Paid subscriptions", Object.values(d.active_subscriptions).reduce((a, b) => a + b, 0)],
  ];
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <h1 className="font-display font-semibold text-2xl text-forest-700">Platform overview</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map(([l, v, to]) => (
          <button key={l} type="button" disabled={!to} onClick={() => to && go(to)} className={`${card} text-left ${to ? "hover:border-forest-600" : "cursor-default"}`}>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
            <div className="mt-1 text-2xl font-display font-semibold text-forest-700">{v}</div></button>
        ))}
      </div>
      <div className={card}><h2 className={`${h2} mb-2`}>Subscriptions by plan</h2>
        {Object.keys(d.active_subscriptions).length ? Object.entries(d.active_subscriptions).map(([k, v]) => <div key={k} className="flex justify-between text-sm py-1 capitalize"><span>{k.replace("_", " ")}</span><span>{v}</span></div>)
          : <div className="text-sm text-foreground/60">No paid subscriptions yet.</div>}</div>
    </div>
  );
}

// --------------------------------------------------------------------- users

const PRODUCT_NAMES = { quran: "Read Qur'an", tajweed: "Tajweed", understand: "Understand Qur'an", qfc: "Quran Fluency", words: "85% Quranic Words", living: "Living with the Qur'an", poems: "Children Poems", stories: "600 Qur'anic Stories", hifz: "Hifz with Hikmah" };
const SOURCE_LABEL = { assigned: "set by an admin", native: "their own role in the app", derived: "automatic", "native-unsynced": "automatic (not yet read from the app)", legacy: "—" };

// Who this person is on the platform, and what role they hold in each app.
function RolesCard({ token, d, reload }) {
  const [busy, setBusy] = useState(null);
  const [picked, setPicked] = useState({});
  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); toast.success(okMsg); await reload(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(null); }
  };
  const byProduct = Object.fromEntries((d.product_roles || []).map((r) => [r.product, r]));
  const uid = d.user.user_id;
  return (
    <div className={card} data-testid="admin-roles">
      <h2 className={`${h2} mb-3`}>Roles</h2>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sand-300 p-3">
        <div>
          <div className="text-sm font-semibold text-forest-700">Platform admin</div>
          <div className="text-xs text-foreground/70">
            {d.platform_admin ? "Can use this console and assign roles." : "Not a platform admin."}
            {d.platform_admin && d.admin_implies_product_admin ? " Right now this also makes them admin in every app." : ""}
            {d.platform_admin_source === "env" ? " (Set in the server settings — change it there.)" : ""}
          </div>
        </div>
        {d.platform_admin_source !== "env" && (
          <button disabled={busy === "pa"} className="btn-primary text-sm disabled:opacity-60" data-testid="toggle-platform-admin"
            onClick={() => {
              if (!d.platform_admin || window.confirm("Remove platform-admin access for this person?")) {
                run("pa", () => adminSetPlatformAdmin(token, uid, !d.platform_admin), d.platform_admin ? "Platform admin removed" : "Now a platform admin");
              }
            }}>
            {d.platform_admin ? "Remove platform admin" : "Make platform admin"}
          </button>
        )}
      </div>

      <table className="w-full mt-4">
        <thead><tr><th className={th}>App</th><th className={th}>Current role</th><th className={th}>Set role</th></tr></thead>
        <tbody>
          {Object.entries(d.assignable_roles || {}).map(([product, options]) => {
            const r = byProduct[product];
            const current = r?.role;
            const choice = picked[product] ?? r?.role ?? "";
            return (
              <tr key={product} data-testid={`role-row-${product}`}>
                <td className={td}>{PRODUCT_NAMES[product] || product}</td>
                <td className={td}>
                  {current ? <><span className="font-semibold">{current}</span> <span className="text-xs text-foreground/60">· {SOURCE_LABEL[r.source] || r.source}</span></>
                    : <span className="text-foreground/60">not opened yet</span>}
                </td>
                <td className={td}>
                  <div className="flex flex-wrap items-center gap-2">
                    <select value={choice} onChange={(e) => setPicked((p) => ({ ...p, [product]: e.target.value }))} className={field} data-testid={`role-select-${product}`}>
                      <option value="" disabled>Choose…</option>
                      {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <button disabled={!choice || busy === product || (r?.source === "assigned" && choice === current)}
                      className="text-sm font-semibold text-forest-700 hover:underline disabled:opacity-40" data-testid={`role-save-${product}`}
                      onClick={() => run(product, () => adminSetProductRole(token, uid, product, choice), `${PRODUCT_NAMES[product]}: role set to ${choice}`)}>
                      Save
                    </button>
                    {r?.source === "assigned" && (
                      <button disabled={busy === product} className="text-sm text-foreground/70 hover:underline" data-testid={`role-clear-${product}`}
                        onClick={() => run(product, () => adminClearProductRole(token, uid, product), "Back to the automatic role")}>
                        Reset
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-3 text-xs text-foreground/60">A role set here applies to this one app only and takes effect the next time the person uses it. Reset goes back to their own/automatic role.</p>
    </div>
  );
}

function EmailEditor({ token, id, email, who, role = "Account", lookupRole = "account", schoolId, keepLabel, onSaved, saveFn, label = "Change e-mail", title = "Change sign-in e-mail", note, saveLabel = "Save e-mail" }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [keep, setKeep] = useState(false);
  const lookup = useEmailLookup((em) => adminEmailCheck(token, lookupRole, em, id, schoolId), open ? val : "");
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await (saveFn ? saveFn(val.trim(), keep) : adminSetUserEmail(token, id, val.trim()));
      toast.success((r && r.message) || "E-mail changed"); setOpen(false); onSaved();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" data-testid="admin-email-edit" title={title} onClick={() => { setVal(""); setKeep(false); setOpen(true); }}
        className="btn-ghost text-xs !px-2.5 !py-1 inline-flex items-center gap-1.5 whitespace-nowrap"><Mail className="h-3.5 w-3.5" /> {label}</button>
      {open && (
        <Modal title={title} onClose={() => setOpen(false)}>
          <form onSubmit={save} className="space-y-4" data-testid="admin-email-modal">
            <div className="rounded-xl bg-sand-100 px-4 py-3">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{role}</div>
              {who && <div className="font-semibold text-forest-700">{who}</div>}
              <div className="text-sm text-foreground/70 break-all">{email}</div>
            </div>
            <label className="block text-sm font-semibold text-forest-700">New e-mail
              <input required autoFocus type="email" className={`${field} mt-1 font-normal`} placeholder="name@example.com" value={val} onChange={(e) => setVal(e.target.value)} data-testid="admin-email-input" /></label>
            <EmailLookupNote info={lookup.info} loading={lookup.loading} />
            {!lookup.info && !lookup.loading && note && <p className="text-xs text-foreground/60">{note}</p>}
            {keepLabel && <label className="flex items-start gap-2 text-sm text-foreground/80"><input type="checkbox" className="mt-1" checked={keep} onChange={(e) => setKeep(e.target.checked)} data-testid="keep-previous-teacher" /><span>{keepLabel}</span></label>}
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" className="btn-primary disabled:opacity-50" disabled={busy || !val.trim() || lookup.blocked} data-testid="admin-email-save">{busy ? "Saving…" : saveLabel}</button>
            </div>
          </form>
        </Modal>)}
    </>
  );
}

// Permanently delete one account. The admin has to type the account's email (or username) to confirm.
function DeleteUserBox({ token, user, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const ident = user.email && !user.email.endsWith("@students.hikmahverses.com") ? user.email : user.username || user.email || "";
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await adminDeleteUser(token, user.user_id, typed.trim()); toast.success(`${user.name} was deleted`); setOpen(false); onDeleted(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className={`${card} border-red-200`} data-testid="delete-user-box">
      <h2 className={h2}>Delete this user</h2>
      <p className="mt-1 text-sm text-foreground/70">Permanently deletes this account and its dashboard data (profile, plan, school place, sessions). It cannot be undone.</p>
      <button type="button" className="btn-ghost mt-3 text-red-700 hover:bg-red-50 inline-flex items-center gap-1.5" onClick={() => { setTyped(""); setOpen(true); }} data-testid="delete-user"><Trash2 className="h-4 w-4" /> Delete user</button>
      {open && (
        <Modal title={`Delete ${user.name}?`} onClose={() => setOpen(false)}>
          <form onSubmit={go} className="space-y-3">
            <p className="text-sm text-foreground/80">This permanently erases the account, its learner profiles, plan, memberships and sign-in sessions, and frees the school licence it used. Payment records and the audit log are kept. Progress stored inside the apps themselves is not touched.</p>
            <label className="block text-sm font-semibold text-forest-700" htmlFor="delete-confirm">Type <span className="font-mono">{ident}</span> to confirm</label>
            <input id="delete-confirm" className={field} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" data-testid="delete-confirm" />
            <button type="submit" disabled={busy || typed.trim().toLowerCase() !== ident.toLowerCase()} className="btn-primary w-full !bg-red-700 hover:!bg-red-800 disabled:opacity-50" data-testid="delete-user-confirm">{busy ? "Deleting…" : "Delete permanently"}</button>
          </form>
        </Modal>)}
    </div>
  );
}

function UserDetail({ token, id, onBack, onDeleted }) {
  const [d, setD] = useState(null);
  const reload = useCallback(() => adminUser(token, id).then(setD), [token, id]);
  useEffect(() => { adminUser(token, id).then(setD).catch((e) => { toast.error(errorMessage(e)); onBack(); }); }, [token, id, onBack]);
  if (!d) return <div className="text-forest-700">Loading…</div>;
  const u = d.user;
  return (
    <div className="space-y-4" data-testid="admin-user-detail">
      <Back onClick={onBack} label="All users" />
      <div className={card}>
        <div className="flex flex-wrap justify-between gap-2"><h2 className={h2}>{u.name}</h2><span className="text-sm font-semibold text-forest-700">{KIND[u.account_kind] || u.account_kind} · {d.plan_name}</span></div>
        <div className="mt-1 text-sm text-foreground/70">{u.email && !u.email.endsWith("@students.hikmahverses.com") ? u.email : `Username ${u.username}`}{d.school ? ` · ${d.school.name}` : ""}</div>
        {u.account_kind !== "student" && <div className="mt-2"><EmailEditor token={token} id={id} email={u.email} who={u.name} role={KIND[u.account_kind] || "Account"} lookupRole={u.account_kind === "teacher" ? "teacher" : "account"} onSaved={reload} note="They sign in with the new e-mail from now on. Their password stays the same." /></div>}
        <div className="text-sm text-foreground/70">Joined {when(u.created_at)} · last active {when(u.last_active_at)}{u.class_name ? ` · class ${u.class_name} ${u.section}` : ""}</div>
      </div>
      <RolesCard token={token} d={d} reload={reload} />
      <ProgressCards apps={d.progress} />
      {d.learner_profiles.length > 0 && <div className={card}><h2 className={`${h2} mb-2`}>Learner profiles</h2>{d.learner_profiles.map((p) => <div key={p.profile_id} className="text-sm py-1">{p.display_name}{p.age_band ? ` · ${p.age_band}` : ""}</div>)}</div>}
      {d.orders.length > 0 && <div className={card}><h2 className={`${h2} mb-2`}>Orders</h2>{d.orders.map((o) => <div key={o.order_id} className="flex justify-between text-sm py-1"><span>{o.plan_id} · {o.interval}{o.mode === "test" ? " (test)" : ""}</span><span>{o.currency} {o.amount} · {o.status}</span></div>)}</div>}
      <DeleteUserBox token={token} user={u} onDeleted={onDeleted || onBack} />
    </div>
  );
}

// Create an account and give it a role (platform admin only): just an email, a temporary password and a role. The person chooses their
// own password at first sign-in and fills in their name and other details later, in their own profile.
const NEW_ROLES = [["student", "Student (normal account)"], ["teacher", "Teacher"], ["principal", "Principal"], ["admin", "Admin"]];
const PW_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";   // no look-alike characters (0/O, 1/l/I)
const makePassword = () => Array.from(crypto.getRandomValues(new Uint32Array(10)), (n) => PW_CHARS[n % PW_CHARS.length]).join("");
function CreateAccountModal({ token, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", password: makePassword(), role: "student" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { onCreated(await adminCreateUser(token, { email: form.email.trim(), password: form.password, role: form.role })); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Create account" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="create-account-form">
        <input required type="email" className={field} placeholder="Email" aria-label="Email" value={form.email} onChange={set("email")} data-testid="create-email" />
        <div className="flex gap-2">
          <input required minLength={8} maxLength={64} className={`${field} font-mono`} placeholder="Temporary password" aria-label="Temporary password" value={form.password} onChange={set("password")} autoComplete="off" data-testid="create-password" />
          <button type="button" className="btn-ghost shrink-0" onClick={() => setForm((f) => ({ ...f, password: makePassword() }))} data-testid="create-generate">Generate</button>
        </div>
        <select className={field} value={form.role} onChange={set("role")} aria-label="Role" data-testid="create-role">
          {NEW_ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="submit" disabled={busy || !form.email.trim() || form.password.length < 8} className="btn-primary w-full disabled:opacity-50" data-testid="create-account-submit">{busy ? "Creating…" : "Create account"}</button>
      </form>
    </Modal>
  );
}

export function AdminUsers({ token }) {
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const [made, setMade] = useState(null);   // the account just created: its temporary password is shown once
  const [tick, setTick] = useState(0);
  const PAGE = 25;
  useEffect(() => {
    const t = setTimeout(() => adminUsers(token, { kind, q, skip: page * PAGE, limit: PAGE }).then(setD).catch((e) => toast.error(errorMessage(e))), 250);
    return () => clearTimeout(t);
  }, [token, kind, q, page, tick]);
  const back = useCallback(() => setSel(null), []);
  if (sel) return <UserDetail token={token} id={sel} onBack={back} onDeleted={() => { setSel(null); setTick((n) => n + 1); }} />;
  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="font-display font-semibold text-2xl text-forest-700 mr-auto">Users</h1>
        <input className={`${field} sm:w-64`} placeholder="Search name, email or username" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} data-testid="admin-user-search" />
        <select className={`${field} sm:w-40`} value={kind} onChange={(e) => { setKind(e.target.value); setPage(0); }} data-testid="admin-user-kind">
          <option value="all">All accounts</option>{Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}s</option>)}</select>
        <button type="button" className="btn-primary inline-flex items-center gap-1.5" onClick={() => setCreating(true)} data-testid="admin-create-account"><UserPlus className="h-4 w-4" /> Create account</button>
      </div>
      {creating && <CreateAccountModal token={token} onClose={() => setCreating(false)} onCreated={(r) => { setCreating(false); setMade(r); setTick((n) => n + 1); }} />}
      {made && <CredentialsModal title="Account created" rows={[{ email: made.email, password: made.password }]} onClose={() => setMade(null)}
        note={`${made.message} This is a temporary password: when they sign in with it, they are asked to set their own before they can continue. They can add their name and other details in their profile afterwards. Nothing was e-mailed, so hand it over yourself.`} />}
      <div className="overflow-x-auto rounded-2xl border border-sand-300 bg-white">
        <table className="w-full"><thead><tr><th className={th}>Name</th><th className={th}>Account</th><th className={th}>Type</th><th className={th}>Plan</th><th className={th}>School</th><th className={th}>Joined</th><th className={th}>Last active</th></tr></thead>
          <tbody>{(d?.users || []).map((u) => (
            <tr key={u.user_id} className="cursor-pointer hover:bg-sand-100" onClick={() => setSel(u.user_id)}>
              <td className={td}>{u.name}</td><td className={td}>{u.email || u.username}</td><td className={td}>{KIND[u.account_kind] || u.account_kind}</td>
              <td className={`${td} capitalize`}>{u.plan_id.replace("_", " ")}</td><td className={td}>{u.school || "—"}</td><td className={td}>{when(u.created_at)}</td><td className={td}>{when(u.last_active_at)}</td></tr>))}
            {d && !d.users.length && <tr><td className={td} colSpan={7}>No users match.</td></tr>}</tbody></table>
      </div>
      {d && (
        <div className="flex items-center justify-between text-sm text-foreground/70">
          <span>{d.total} user{d.total !== 1 ? "s" : ""}</span>
          <span className="flex gap-2">
            <button type="button" className="btn-ghost" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Previous</button>
            <button type="button" className="btn-ghost" disabled={(page + 1) * PAGE >= d.total} onClick={() => setPage((p) => p + 1)}>Next</button></span>
        </div>)}
    </div>
  );
}

// ------------------------------------------------------------------- schools

function SchoolLicenceBox({ token, d, reload }) {
  const lic = d.school.license || null;
  const declared = d.school.onboarding && d.school.onboarding.declared_count;
  const [seats, setSeats] = useState(lic ? String(lic.seats) : "");
  const [kind, setKind] = useState((lic && lic.kind) || "standard");
  const [days, setDays] = useState(lic ? "" : "365");   // an existing licence keeps its end date unless this is filled in
  const [apps, setApps] = useState(lic && lic.apps && lic.apps.length ? lic.apps : lic ? LICENSE_APPS.map(([id]) => id) : DEFAULT_LICENSE_APPS);   // an existing licence keeps its apps (none picked = every app); a new one starts with Poems and Stories
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!apps.length) return toast.error("Choose at least one app for this licence");
    setBusy(true);
    try { await adminSetSchoolLicense(token, d.school.school_id, { seats: Number(seats), kind, ...(days ? { days: Number(days) } : { valid_until: lic.valid_until.slice(0, 10) }), apps: apps.length === LICENSE_APPS.length ? null : apps }); toast.success("Licence saved"); await reload(); }
    catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <form className={card} onSubmit={save} data-testid="school-licence-box">
      <h2 className={h2}>Licence</h2>
      <p className="text-sm text-foreground/70 mt-1">
        {lic ? `${lic.used} of ${lic.seats} places in use, valid until ${when(lic.valid_until)}.` : "No licence yet, so the principal cannot add people."}
        {declared ? ` The principal said they will onboard ${declared}.` : ""}
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-forest-700">Places<input className={`${field} mt-1 w-28`} type="number" min={0} required value={seats} onChange={(e) => setSeats(e.target.value)} /></label>
        <label className="text-xs font-semibold text-forest-700">Kind<select className={`${field} mt-1 w-32`} value={kind} onChange={(e) => setKind(e.target.value)}><option value="standard">Standard</option><option value="pilot">Pilot</option></select></label>
        <label className="text-xs font-semibold text-forest-700">Days from today<input className={`${field} mt-1 w-28`} type="number" min={1} required={!lic} placeholder={lic ? "keep" : ""} value={days} onChange={(e) => setDays(e.target.value)} /></label>
        <button type="submit" className="btn-primary" disabled={busy}>{busy ? "Saving…" : lic ? "Change licence" : "Approve and set licence"}</button>
      </div>
      <div className="mt-3"><AppPicker value={apps} onChange={setApps} /></div>
    </form>);
}

// Apps the principal asked to add to the school's licence: approving adds the app (seats and end date stay the same).
function AppRequests({ token, schoolId, onChanged }) {
  const [rows, setRows] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminSchoolAppRequests(token, "pending", schoolId).then((d) => setRows(d.requests)).catch((e) => toast.error(errorMessage(e))), [token, schoolId]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setDeclining(null); setNote(""); await load(); if (onChanged) onChanged(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!rows || !rows.length) return null;
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="app-requests">
      <div className="font-display font-semibold text-forest-700">Apps the principal asked for ({rows.length})</div>
      {rows.map((r) => (
        <div key={r.request_id} className="rounded-xl bg-white border border-sand-300 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="mr-auto"><div className="font-semibold text-forest-700">{r.product_name}</div>
              <div className="text-xs text-foreground/60">Asked {when(r.created_at)}{r.requested_by_email ? ` · ${r.requested_by_email}` : ""}{r.message ? ` · “${r.message}”` : ""}</div></div>
            <button type="button" className="btn-primary" disabled={busy} data-testid="approve-app" onClick={() => act(() => adminAppRequestApprove(token, r.request_id), `${r.product_name} added to the licence`)}>Add to licence</button>
            <button type="button" className="btn-ghost text-red-700" disabled={busy} data-testid="decline-app" onClick={() => setDeclining(declining === r.request_id ? null : r.request_id)}>Decline</button>
          </div>
          {declining === r.request_id && (
            <div className="mt-3 flex flex-wrap gap-2"><input className={`${field} sm:w-96`} placeholder="Reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
              <button type="button" className="btn-ghost text-red-700" disabled={busy} onClick={() => act(() => adminAppRequestDecline(token, r.request_id, note.trim()), "Declined")}>Confirm decline</button></div>)}
        </div>))}
    </div>);
}

// Delete a school (admin): asks the admin to type the school's name first. Teachers who registered themselves go back to individual
// accounts; every other member account is deactivated; classes and student records are removed.
function DeleteSchool({ token, school, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (school.demo) return null;   // the demo school can't be deleted
  const go = async () => {
    setBusy(true);
    try { await adminDeleteSchool(token, school.school_id, name); toast.success(`${school.name} was deleted`); onDeleted(); }
    catch (e) { toast.error(errorMessage(e)); setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50/40 p-4" data-testid="delete-school-box">
      <div className="font-display font-semibold text-red-800">Delete this school</div>
      {!open ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <p className="text-sm text-foreground/70 mr-auto">Ends its licence and removes its classes and student records. Teachers who registered themselves go back to normal accounts; other teacher and student accounts are deactivated. The principal keeps their account.</p>
          <button type="button" className="btn-ghost text-red-700 border border-red-300" onClick={() => setOpen(true)} data-testid="delete-school">Delete school</button>
        </div>
      ) : (
        <div className="mt-2 space-y-2">
          <p className="text-sm text-foreground/80">This can&apos;t be undone from the dashboard. Type <b>{school.name}</b> to confirm.</p>
          <div className="flex flex-wrap gap-2">
            <input className={`${field} sm:w-96`} placeholder={school.name} value={name} onChange={(e) => setName(e.target.value)} data-testid="delete-school-name" />
            <button type="button" className="btn-primary !bg-red-700 disabled:opacity-50" disabled={busy || name.trim().toLowerCase() !== school.name.trim().toLowerCase()} onClick={go} data-testid="delete-school-yes">{busy ? "Deleting…" : "Delete school"}</button>
            <button type="button" className="btn-ghost" disabled={busy} onClick={() => { setOpen(false); setName(""); }}>Keep it</button>
          </div>
        </div>)}
    </div>);
}

function SchoolDetail({ token, id, onBack, onChanged, onDeleted }) {
  const [d, setD] = useState(null);
  const [stu, setStu] = useState(null);
  const reload = useCallback(() => adminSchool(token, id).then(setD).catch((e) => { toast.error(errorMessage(e)); onBack(); }), [token, id, onBack]);
  useEffect(() => { reload(); }, [reload]);
  const openStudent = async (s) => {
    try { setStu({ ...(await adminSchoolStudent(token, id, s.user_id)), name: s.name }); } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!d) return <div className="text-forest-700">Loading…</div>;
  if (stu) {
    const s = stu.student;
    return (
      <div className="space-y-4" data-testid="admin-student-detail">
        <Back onClick={() => setStu(null)} label={d.school.name} />
        <div className={card}><h2 className={h2}>{s.name}</h2>
          <div className="mt-1 text-sm text-foreground/70">Username {s.username} · Class {s.class_name} {s.section} · last active {when(s.last_active_at)}</div>
          <div className="mt-3 font-semibold text-forest-700">{stu.progress.reported ? `${stu.progress.percent}% overall` : "No progress reported yet"}</div><div className="mt-1"><Bar percent={stu.progress.percent} /></div></div>
        <ProgressCards apps={stu.progress.apps} />
      </div>
    );
  }
  return (
    <div className="space-y-5" data-testid="admin-school-detail">
      <Back onClick={onBack} label="All schools" />
      <div className={card}>
        <div className="flex flex-wrap justify-between gap-2"><h2 className={h2}>{d.school.name}</h2><span className="text-sm font-semibold text-forest-700">{d.plan_name} plan</span></div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-foreground/70">
          <span>Principal: <span className="font-semibold text-forest-700">{d.principal.name}</span> ({d.principal.email})</span>
          {d.principal.user_id && <EmailEditor token={token} id={d.principal.user_id} email={d.principal.email} who={d.principal.name} role="Current principal" lookupRole="principal" schoolId={id} keepLabel={`Keep ${d.principal.name} in this school as a teacher (uses one licence place)`} label="Change principal" title="Change principal" saveLabel="Change principal"
            note="Type the e-mail of the new principal. If that person already has a normal HikmahVerses account, or is a teacher of this school, they become its principal and the current principal goes back to a normal account (or stays as a teacher if you tick the box). Someone who is already a principal, or belongs to another school, can't be used. If no account has that e-mail, the current principal's sign-in e-mail is simply changed."
            saveFn={(em, keep) => adminSetSchoolPrincipal(token, id, em, keep)} onSaved={() => { reload(); if (onChanged) onChanged(); }} />}
          <span>· last active {when(d.principal.last_active_at)}</span></div>
        <div className="text-sm text-foreground/70">{d.teachers.length} teachers · {d.students.length} students · average progress {d.average_percent}%</div>
      </div>
      <ClassApprovals token={token} schoolId={id} onChanged={() => { reload(); if (onChanged) onChanged(); }} />
      <AppRequests token={token} schoolId={id} onChanged={() => { reload(); if (onChanged) onChanged(); }} />
      <SchoolLicenceBox token={token} d={d} reload={reload} />
      <div className="overflow-x-auto rounded-2xl border border-sand-300 bg-white">
        <div className="px-4 pt-3 font-display font-semibold text-forest-700">Teachers</div>
        <table className="w-full"><thead><tr><th className={th}>Teacher</th><th className={th}>Classes</th><th className={th}>Students</th><th className={th}>Avg progress</th><th className={th}>Last sign-in</th></tr></thead>
          <tbody>{d.teachers.map((t) => <tr key={t.user_id}><td className={td}><div className="font-semibold text-forest-700">{t.name}</div><div className="mt-0.5 flex flex-wrap items-center gap-2"><span className="text-xs text-foreground/60 break-all">{t.email}</span><EmailEditor token={token} id={t.user_id} email={t.email} who={t.name} role="Teacher" lookupRole="teacher" onSaved={reload} note="If this person already has a normal HikmahVerses account, they take over this teacher's classes and students and use that account from now on. Otherwise the e-mail the teacher signs in with is just updated." /></div></td>
            <td className={td}>{t.classes.map((c) => `${c.class_name} ${c.section}`).join(", ") || "—"}</td><td className={td}>{t.student_count}</td><td className={td}>{t.student_count ? `${t.average_percent}%` : "—"}</td><td className={td}>{when(t.last_active_at)}</td></tr>)}
            {!d.teachers.length && <tr><td className={td} colSpan={5}>No teachers.</td></tr>}</tbody></table>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-sand-300 bg-white">
        <div className="px-4 pt-3 font-display font-semibold text-forest-700">Students</div>
        <table className="w-full" data-testid="admin-students-table"><thead><tr><th className={th}>Student</th><th className={th}>Class</th><th className={th}>Progress</th><th className={th}>Last active</th></tr></thead>
          <tbody>{d.students.map((s) => <tr key={s.user_id} className={s.record ? "" : "cursor-pointer hover:bg-sand-100"} onClick={() => { if (!s.record) openStudent(s); }}>
            <td className={td}>{s.name}<div className="text-xs text-foreground/60">{s.record ? `Link code ${s.link_code}` : s.username}</div></td><td className={td}>{s.class_name} {s.section}</td>
            <td className={`${td} w-56`}><div className="flex items-center gap-2"><div className="flex-1"><Bar percent={s.percent} /></div><span className="text-xs w-10">{s.reported ? `${s.percent}%` : "—"}</span></div></td><td className={td}>{when(s.last_active_at)}</td></tr>)}
            {!d.students.length && <tr><td className={td} colSpan={4}>No students.</td></tr>}</tbody></table>
      </div>
      <DeleteSchool token={token} school={d.school} onDeleted={onDeleted} />
    </div>
  );
}

// Classes a teacher sent for approval: shown inside the school they belong to, never on the all-schools list.
function ClassApprovals({ token, schoolId, onChanged }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => adminClasses(token, "pending", schoolId).then((d) => setRows(d.classes)).catch((e) => toast.error(errorMessage(e))), [token, schoolId]);
  useEffect(() => { load(); }, [load]);
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setOpen(null); setNote(""); await load(); if (onChanged) onChanged(); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!rows || !rows.length) return null;
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="class-approvals">
      <div className="font-display font-semibold text-forest-700">Classes waiting for approval ({rows.length})</div>
      {rows.map((c) => (
        <div key={c.class_id} className="rounded-xl bg-white border border-sand-300 p-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="mr-auto"><div className="font-semibold text-forest-700">{c.school_name} · {c.class_name} {c.section}</div>
              <div className="text-xs text-foreground/60">Teacher {c.teacher_name} · {c.students.length} students · {c.unlimited ? "no seat limit" : `${c.seats_left} seats left`} · sent {when(c.submitted_at)}</div></div>
            <button type="button" className="btn-ghost" onClick={() => setOpen(open === c.class_id ? null : c.class_id)}>{open === c.class_id ? "Hide" : "View students"}</button>
            <button type="button" className="btn-primary" disabled={busy} data-testid="approve-class" onClick={() => act(() => adminClassApprove(token, c.class_id), "Approved — students added")}>Approve</button>
            <button type="button" className="btn-ghost text-red-700" disabled={busy} data-testid="reject-class" onClick={() => setOpen(c.class_id)}>Reject</button>
          </div>
          {open === c.class_id && (
            <div className="mt-3 space-y-2">
              <div className="overflow-x-auto"><table className="w-full"><thead><tr><th className={th}>#</th><th className={th}>Name</th><th className={th}>Age</th><th className={th}>Gender</th><th className={th}>Parent mobile</th><th className={th}>Parent e-mail</th></tr></thead>
                <tbody>{c.students.map((s, i) => <tr key={i}><td className={td}>{i + 1}</td><td className={td}>{s.name}</td><td className={td}>{s.age || "—"}</td><td className={td}>{s.gender === "male" ? "Boy" : s.gender === "female" ? "Girl" : "—"}</td><td className={td}>{s.parent_mobile || "—"}</td><td className={td}>{s.parent_email || "—"}</td></tr>)}</tbody></table></div>
              <div className="flex flex-wrap gap-2"><input className={`${field} sm:w-96`} placeholder="Reason for rejecting (the teacher sees this)" value={note} onChange={(e) => setNote(e.target.value)} />
                <button type="button" className="btn-ghost text-red-700" disabled={busy || !note.trim()} onClick={() => act(() => adminClassReject(token, c.class_id, note.trim()), "Rejected")}>Confirm reject</button></div>
            </div>)}
        </div>))}
    </div>);
}

export function AdminSchools({ token, pending = {}, onPendingChanged }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const [tick, setTick] = useState(0);   // bumped after a school is deleted, so the list reloads
  useEffect(() => {
    const t = setTimeout(() => adminSchools(token, q).then((d) => setRows(d.schools)).catch((e) => toast.error(errorMessage(e))), 250);
    return () => clearTimeout(t);
  }, [token, q, tick]);
  const back = useCallback(() => setSel(null), []);
  if (sel) return <SchoolDetail token={token} id={sel} onBack={back} onChanged={onPendingChanged} onDeleted={() => { setSel(null); setTick((t) => t + 1); if (onPendingChanged) onPendingChanged(); }} />;
  return (
    <div className="space-y-4" data-testid="admin-schools">
      <div className="flex flex-wrap items-center gap-2"><h1 className="font-display font-semibold text-2xl text-forest-700 mr-auto">Schools</h1>
        <input className={`${field} sm:w-64`} placeholder="Search schools" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <div className="overflow-x-auto rounded-2xl border border-sand-300 bg-white">
        <table className="w-full"><thead><tr><th className={th}>School</th><th className={th}>Principal</th><th className={th}>Plan</th><th className={th}>Teachers</th><th className={th}>Students</th><th className={th}>Created</th></tr></thead>
          <tbody>{(rows || []).map((s) => <tr key={s.school_id} className="cursor-pointer hover:bg-sand-100" onClick={() => setSel(s.school_id)}>
            <td className={td}>{s.name}{pending[s.school_id] > 0 && <span className="ml-2 inline-grid place-items-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold align-middle" title={`${pending[s.school_id]} class${pending[s.school_id] === 1 ? "" : "es"} waiting for approval`} data-testid={`school-pending-${s.school_id}`}>{pending[s.school_id]}</span>}</td><td className={td}>{s.principal.name}<div className="text-xs text-foreground/60">{s.principal.email}</div></td><td className={`${td} capitalize`}>{s.plan_id}</td>
            <td className={td}>{s.teacher_count}</td><td className={td}>{s.student_count}</td><td className={td}>{when(s.created_at)}</td></tr>)}
            {rows && !rows.length && <tr><td className={td} colSpan={6}>No schools yet.</td></tr>}</tbody></table>
      </div>
    </div>
  );
}
